import { Marquee } from '@sbs/ui-core';
import { Reveal } from '../components/Reveal';
import { ScrollProgressBar } from '../components/ScrollProgressBar';
import { PageMeta } from '../components/PageMeta';
import { Header as NightFlightHeader, Footer as NightFlightFooter, Hero as NightFlightHero, Strip as NightFlightStrip, Growth as NightFlightGrowth, Positioning as NightFlightPositioning, Reasons as NightFlightReasons, About as NightFlightAbout, Cta as NightFlightCta } from '../components/home/NightFlight';

export function HomePage() {
  // #10764: the Night Flight header and footer render on / only (App.tsx `ownChrome`); every other
  // page keeps the site header and footer. The footer carries the page's closing call to action.
  return (
    <>
    <NightFlightHeader />
    <main className="relative">
      <PageMeta
        title="Small Business Consulting | Strategy & Execution"
        description="Small business consulting that combines strategy with hands-on execution: marketing, sales, operations, finance and leadership. One partner, not a slide deck."
        path="/"
        keywords={['small business consulting', 'business strategy', 'small business growth']}
      />
      {/* Scroll Progress Bar */}
      <ScrollProgressBar />

      {/* Night Flight: hero (promoted from the Design Studio) */}
      <div data-lab-slot="hero"><NightFlightHero /></div>

      {/* Night Flight: strip (promoted from the Design Studio) */}
      <div data-lab-slot="strip"><NightFlightStrip /></div>

      {/* Night Flight: growth (promoted from the Design Studio) */}
      <div data-lab-slot="growth"><NightFlightGrowth /></div>

      {/* Night Flight: positioning (promoted from the Design Studio) */}
      <div data-lab-slot="positioning"><NightFlightPositioning /></div>

      {/* Night Flight: reasons (promoted from the Design Studio) */}
      <div data-lab-slot="reasons"><NightFlightReasons /></div>

      {/* Quality Work Callout */}
      <section data-home-section="quality" className="py-16 px-4 sm:px-6 lg:px-8 bg-linear-to-b from-background dark:from-card to-primary/5">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl p-10 sm:p-12 bg-card/80 border border-border sbi-shadow-lg text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Quality Work. <span className="text-primary">Swift Delivery.</span>{' '}
              <span className="text-accent">Real Results.</span>
            </h2>
            <p className="text-lg text-foreground/80 max-w-3xl mx-auto">
              Fast and good, built for small business. We deliver full-stack strategy, execution,
              and hands-on support that move the business forward. All the lift, none of the drag.
            </p>
          </div>
        </div>
      </section>

      {/* Night Flight: about (promoted from the Design Studio) */}
      <div data-lab-slot="about"><NightFlightAbout /></div>

      {/* Testimonials Section */}
      <section data-home-section="testimonials" className="py-24 px-4 sm:px-6 lg:px-8 bg-linear-to-b from-primary/5 to-background dark:to-card">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="text-primary">What Our Clients Say</span>
            </h2>
            <p className="text-xl text-foreground/80 max-w-3xl mx-auto">
              Real results from real small businesses. Here's how we've helped.
            </p>
          </div>

          <Reveal className="mb-12">
            <Marquee aria-label="Client testimonials" speed={40}>
            {/* Testimonial 1 */}
            <div className="inline-block w-[22rem] sm:w-[24rem] mx-3 sm:mx-4 p-8 rounded-2xl bg-card border border-border sbi-shadow-md hover:sbi-shadow-lg transition-all duration-300 hover:-translate-y-1 align-top whitespace-normal">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-sunshine text-xl">
                    ★
                  </span>
                ))}
              </div>
              <p className="text-foreground/80 mb-6 text-lg leading-relaxed">
                "Working artist who discovered that a better-run business creates more space for
                creativity, not less."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                  R
                </div>
                <div>
                  <p className="font-bold">Rob</p>
                  <p className="text-sm text-foreground/60">Artist, Independent Artist</p>
                </div>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="inline-block w-[22rem] sm:w-[24rem] mx-3 sm:mx-4 p-8 rounded-2xl bg-card border border-border sbi-shadow-md hover:sbi-shadow-lg transition-all duration-300 hover:-translate-y-1 align-top whitespace-normal">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-sunshine text-xl">
                    ★
                  </span>
                ))}
              </div>
              <p className="text-foreground/80 mb-6 text-lg leading-relaxed">
                "After the quietest period in his career, Matt is back at work and expanding his
                client base after working with SmallBiz Innovation."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                  M
                </div>
                <div>
                  <p className="font-bold">Matt</p>
                  <p className="text-sm text-foreground/60">
                    Creative Professional, Independent Professional
                  </p>
                </div>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="inline-block w-[22rem] sm:w-[24rem] mx-3 sm:mx-4 p-8 rounded-2xl bg-card border border-border sbi-shadow-md hover:sbi-shadow-lg transition-all duration-300 hover:-translate-y-1 align-top whitespace-normal">
              <div className="flex gap-1 mb-4">
                {[...Array(5)].map((_, i) => (
                  <span key={i} className="text-sunshine text-xl">
                    ★
                  </span>
                ))}
              </div>
              <p className="text-foreground/80 mb-6 text-lg leading-relaxed">
                "Great team of experts who quickly understood goals, addressed issues, and created a
                clear plan – highly recommended."
              </p>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                  L
                </div>
                <div>
                  <p className="font-bold">Lauren</p>
                  <p className="text-sm text-foreground/60">SmallBiz Client</p>
                </div>
              </div>
            </div>
          </Marquee>
          </Reveal>
        </div>
      </section>

      {/* Tools Marquee — Motion standard S4 (#7781): one tool strip on the S3 Marquee.
          Existing approved copy (the hub topic names), reduced-motion stops the loop. */}
      <section data-home-section="tools" className="py-16 px-4 sm:px-6 lg:px-8 bg-linear-to-b from-background dark:from-card to-primary/5 overflow-hidden">
        <Reveal className="text-center mb-10">
          <p className="text-sm font-semibold text-primary tracking-wide mb-2">Explore by topic</p>
          <h2 className="text-3xl sm:text-4xl font-bold">Tools, guides, and support to help you grow</h2>
        </Reveal>
        <Marquee aria-label="Topic tools" speed={35}>
          {['Strategy', 'Operations', 'Marketing', 'Sales', 'Finance', 'Leadership'].map((topic) => (
            <span
              key={topic}
              className="inline-block mx-6 px-6 py-3 rounded-xl bg-card border border-border sbi-shadow-sm text-lg font-semibold text-foreground"
            >
              {topic}
            </span>
          ))}
        </Marquee>
      </section>

      {/* Night Flight: cta (promoted from the Design Studio) */}
      <div data-lab-slot="cta"><NightFlightCta /></div>
    </main>
    <NightFlightFooter />
    </>
  );
}
